import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  GraduationCap, Search, Phone, Mail, FileText, Download, ChevronDown, ChevronUp, User, Eye,
  Award, Camera
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';

const ResponsableFormateurs = ({ user }) => {
  const [formateurs, setFormateurs] = useState([]);
  const [centreNom, setCentreNom] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFormateur, setExpandedFormateur] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [formRes, centreRes] = await Promise.all([
        axios.get('/responsable/formateurs'),
        axios.get('/responsable/mon-centre')
      ]);
      
      // Enrichir avec profil complet et documents
      const enriched = await Promise.all((formRes.data || []).map(async (formateur) => {
        try {
          const profileRes = await axios.get(`/formateurs/${formateur.user_id}`).catch(() => ({ data: {} }));
          const docsRes = await axios.get(`/documents/${formateur.user_id}`).catch(() => ({ data: [] }));
          return { 
            ...formateur, 
            ...profileRes.data?.profile,
            documents: docsRes.data || [] 
          };
        } catch (e) {
          return { ...formateur, documents: [] };
        }
      }));
      
      setFormateurs(enriched);
      setCentreNom(centreRes.data?.nom || '');
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Voir un document
  const viewDocument = async (documentId) => {
    try {
      toast.info('Chargement du document...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/pdf' });
        openBlobInNewTab(blob);
      } else {
        toast.error('Document non disponible');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du document');
    }
  };

  // Télécharger un document
  const downloadDocument = async (documentId, filename) => {
    try {
      toast.info('Téléchargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Document téléchargé');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const filteredFormateurs = formateurs.filter(f => {
    const fullName = `${f.prenom || ''} ${f.nom || ''}`.toLowerCase();
    const email = f.email || '';
    return `${fullName} ${email}`.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="responsable-formateurs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Consultation seule</span>
          </div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A]">
            Formateurs - Centre {centreNom}
          </h1>
          <p className="text-slate-600">{formateurs.length} formateur(s) assigné(s)</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Rechercher un formateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-4">
        <GraduationCap className="h-8 w-8 text-green-600" />
        <div>
          <p className="text-2xl font-bold text-green-700">{formateurs.length}</p>
          <p className="text-sm text-green-600">Formateurs dans ce centre</p>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-4">
        {filteredFormateurs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <GraduationCap className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun formateur assigné à ce centre</p>
          </div>
        ) : (
          filteredFormateurs.map(formateur => {
            const isExpanded = expandedFormateur === formateur.user_id;
            const nom = formateur.nom || '';
            const prenom = formateur.prenom || '';
            const fullName = `${prenom} ${nom}`.trim() || 'Formateur';
            const diplomes = formateur.documents?.filter(d => (d.document_type || d.type || '').toLowerCase().includes('diplome')) || [];
            const autresDocs = formateur.documents?.filter(d => !(d.document_type || d.type || '').toLowerCase().includes('diplome')) || [];
            
            return (
              <div
                key={formateur.user_id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-all"
              >
                {/* En-tête */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedFormateur(isExpanded ? null : formateur.user_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-18 rounded-md overflow-hidden bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center text-white text-xl font-bold">
                        {(formateur.photo || formateur.photo_identite || formateur.profile?.photo) ? (
                          <img src={formateur.photo || formateur.photo_identite || formateur.profile?.photo} alt={fullName} className="w-full h-full object-cover" />
                        ) : (
                          fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-[#0F172A]">
                          {formateur.genre || ''} {fullName}
                        </h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          {formateur.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {formateur.email}
                            </span>
                          )}
                          {formateur.telephone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {formateur.telephone}
                            </span>
                          )}
                        </div>
                        {formateur.diplome && (
                          <p className="mt-1 text-sm text-[#8DC63F] font-medium">
                            {formateur.diplome} {formateur.annee_diplome && `(${formateur.annee_diplome})`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {diplomes.length} diplôme(s)
                      </span>
                      <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                        {autresDocs.length} doc(s)
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Détails dépliés */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-gradient-to-b from-green-50 to-white p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      
                      {/* Colonne 1: Profil complet */}
                      <div className="bg-white p-5 rounded-xl border border-green-200 shadow-sm">
                        <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                          <User className="h-5 w-5" />
                          Profil Complet
                        </h4>
                        
                        <div className="flex justify-center mb-4">
                          <div className="w-20 h-28 rounded-md overflow-hidden bg-green-100 border-4 border-green-200">
                            {formateur.photo ? (
                              <img src={formateur.photo} alt={fullName} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-green-400">
                                <Camera className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Genre</span>
                            <span className="font-medium">{formateur.genre || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Nom</span>
                            <span className="font-medium">{nom || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Prénom</span>
                            <span className="font-medium">{prenom || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Âge</span>
                            <span className="font-medium">{formateur.age || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Téléphone</span>
                            <span className="font-medium">{formateur.telephone || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Email</span>
                            <span className="font-medium text-xs">{formateur.email || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Diplôme principal</span>
                            <span className="font-medium">{formateur.diplome || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Année diplôme</span>
                            <span className="font-medium">{formateur.annee_diplome || '-'}</span>
                          </div>
                        </div>

                        {formateur.signature && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="text-sm text-slate-500 mb-2">Signature</p>
                            <img src={formateur.signature} alt="Signature" className="max-h-16 border border-slate-200 rounded" />
                          </div>
                        )}
                      </div>

                      {/* Colonne 2: Diplômes */}
                      <div className="bg-white p-5 rounded-xl border border-green-200 shadow-sm">
                        <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                          <Award className="h-5 w-5" />
                          Diplômes ({diplomes.length})
                        </h4>
                        {diplomes.length === 0 ? (
                          <div className="text-center py-8 bg-slate-50 rounded-lg">
                            <Award className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">Aucun diplôme uploadé</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {diplomes.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                                <div className="flex items-center gap-2">
                                  <Award className="h-4 w-4 text-green-600" />
                                  <span className="text-sm font-medium text-green-800 truncate max-w-[150px]">
                                    {doc.document_type || doc.type || 'Diplôme'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => viewDocument(doc.document_id)}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Voir"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'diplome'}_${fullName}`)}
                                    className="p-2 text-green-500 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Colonne 3: Autres documents */}
                      <div className="bg-white p-5 rounded-xl border border-green-200 shadow-sm">
                        <h4 className="font-semibold text-green-800 mb-4 flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          Autres Documents ({autresDocs.length})
                        </h4>
                        {autresDocs.length === 0 ? (
                          <div className="text-center py-8 bg-slate-50 rounded-lg">
                            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">Aucun autre document</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {autresDocs.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-slate-600" />
                                  <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">
                                    {doc.document_type || doc.type || 'Document'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => viewDocument(doc.document_id)}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Voir"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'doc'}_${fullName}`)}
                                    className="p-2 text-green-500 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ResponsableFormateurs;
